import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  cambiarActivoFlujo,
  crearFlujo,
  enviarPrueba,
  guardarBorrador,
  listarEnvios,
  listarFlujos,
  listarVersiones,
  obtenerCatalogo,
  obtenerEnvio,
  obtenerFlujo,
  obtenerVersion,
  publicarFlujo,
  reenviarEnvio,
  renombrarFlujo,
  type GuardarBorradorInput,
  type ListarEnviosParams,
  type ListarFlujosParams,
} from "../services/notificaciones-service";

// --- Queries ---------------------------------------------------------------------

/** El catálogo solo cambia con un despliegue (sale del código) · cache larga. */
export const catalogoNotificacionesQueryOptions = queryOptions({
  queryKey: qk.notifCatalogo,
  queryFn: obtenerCatalogo,
  staleTime: 30 * 60_000,
});

export function useCatalogoNotificaciones() {
  return useSuspenseQuery(catalogoNotificacionesQueryOptions);
}

export const flujosQueryOptions = (params: ListarFlujosParams = {}) =>
  queryOptions({
    queryKey: [...qk.notifFlujos, params] as const,
    queryFn: () => listarFlujos(params),
    placeholderData: keepPreviousData,
  });

export function useFlujos(params: ListarFlujosParams) {
  return useQuery(flujosQueryOptions(params));
}

export const flujoQueryOptions = (id: string) =>
  queryOptions({ queryKey: qk.notifFlujo(id), queryFn: () => obtenerFlujo(id) });

export function useFlujo(id: string) {
  return useSuspenseQuery(flujoQueryOptions(id));
}

export function useVersiones(id: string, habilitado: boolean) {
  return useQuery({ queryKey: qk.notifVersiones(id), queryFn: () => listarVersiones(id), enabled: habilitado });
}

export function useVersion(id: string, numero: number | null) {
  return useQuery({
    queryKey: [...qk.notifVersiones(id), numero] as const,
    queryFn: () => obtenerVersion(id, numero!),
    enabled: numero !== null,
  });
}

export const enviosQueryOptions = (params: ListarEnviosParams = {}) =>
  queryOptions({
    queryKey: [...qk.notifEnvios, params] as const,
    queryFn: () => listarEnvios(params),
    placeholderData: keepPreviousData,
  });

export function useEnvios(params: ListarEnviosParams) {
  return useQuery(enviosQueryOptions(params));
}

export function useEnvio(id: string | null) {
  return useQuery({ queryKey: qk.notifEnvio(id ?? ""), queryFn: () => obtenerEnvio(id!), enabled: id !== null });
}

// --- Mutations -------------------------------------------------------------------

export function useCrearFlujo(opts?: { onCreado?: (id: string) => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: crearFlujo,
    onSuccess: (d) => {
      toastExito({ title: `Flujo "${d.flujo.nombre}" creado`, message: "Arrastra eventos y notificaciones al lienzo." });
      void qc.invalidateQueries({ queryKey: qk.notifFlujos });
      opts?.onCreado?.(d.flujo.id);
    },
    onError: toastErrorApi,
  });
}

export function useRenombrarFlujo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { nombre: string; descripcion: string | null }) => renombrarFlujo(id, input),
    onSuccess: () => {
      toastExito({ title: "Flujo actualizado" });
      void qc.invalidateQueries({ queryKey: qk.notifFlujos });
      void qc.invalidateQueries({ queryKey: qk.notifFlujo(id) });
    },
    onError: toastErrorApi,
  });
}

export function useCambiarActivoFlujo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => cambiarActivoFlujo(id, activo),
    onSuccess: (f) => {
      toastExito({
        title: f.activo ? "Flujo activado" : "Flujo desactivado",
        message: f.activo ? "Vuelve a notificar con su versión publicada." : "Deja de enviar notificaciones hasta reactivarlo.",
      });
      void qc.invalidateQueries({ queryKey: qk.notifFlujos });
      void qc.invalidateQueries({ queryKey: qk.notifFlujo(f.id) });
    },
    onError: toastErrorApi,
  });
}

export function useGuardarBorrador(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GuardarBorradorInput) => guardarBorrador(id, input),
    onSuccess: (d) => {
      qc.setQueryData(qk.notifFlujo(id), d);
      void qc.invalidateQueries({ queryKey: qk.notifFlujos });
    },
    onError: toastErrorApi,
  });
}

export function usePublicarFlujo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publicarFlujo(id),
    onSuccess: (d) => {
      toastExito({ title: `Versión ${d.vigente?.numero ?? ""} publicada`, message: "El motor ya usa esta versión." });
      qc.setQueryData(qk.notifFlujo(id), d);
      void qc.invalidateQueries({ queryKey: qk.notifFlujos });
      void qc.invalidateQueries({ queryKey: qk.notifVersiones(id) });
    },
    // Con lista de errores (borrador no publicable) el diseñador la muestra en un banner.
    onError: (err) => {
      const errores = (err as { response?: { data?: { errores?: string[] } } }).response?.data?.errores;
      if (!errores || errores.length === 0) toastErrorApi(err);
    },
  });
}

export function useEnviarPrueba(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { notificacionClave: string; destino: string | null; tipoEvento: string | null }) =>
      enviarPrueba(id, input),
    onSuccess: (d) => {
      if (d.envio.estado === "Enviado") toastExito({ title: "Prueba enviada", message: `A ${d.envio.destinatarioNombre ?? d.envio.destinatario}` });
      else toastErrorApi(new Error(d.envio.ultimoError ?? `La prueba quedó en estado ${d.envio.estado}.`));
      void qc.invalidateQueries({ queryKey: qk.notifEnvios });
    },
    onError: toastErrorApi,
  });
}

export function useReenviarEnvio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reenviarEnvio,
    onSuccess: (d) => {
      if (d.envio.estado === "Enviado") toastExito({ title: "Reenviado", message: `A ${d.envio.destinatarioNombre ?? d.envio.destinatario}` });
      else toastErrorApi(new Error(d.envio.ultimoError ?? `Quedó en estado ${d.envio.estado}.`));
      qc.setQueryData(qk.notifEnvio(d.envio.id), d);
      void qc.invalidateQueries({ queryKey: qk.notifEnvios });
    },
    onError: toastErrorApi,
  });
}
